/**
 * Hook useKanbanColumnCounts - Busca contagens reais por fase com filtros
 * Chamado apenas quando há filtros ativos no Kanban, retornando COUNT/SUM do banco
 * sem limitação de paginação.
 * Versão: 1.1.0 - Adicionado suporte ao filtro por tags
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { DealFilters } from '@/hooks/useCrmDeals';
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('KanbanColumnCounts');

// Tipo de retorno da RPC
interface ColumnCount {
  phase_id: string;
  deal_count: number;
  total_value: number;
}

/**
 * Verifica se algum filtro relevante está ativo
 * Ignora pipeline_id, phase_id e workspace_id pois são filtros estruturais
 */
export function hasActiveFilters(filters?: DealFilters): boolean {
  if (!filters) return false;
  return !!(
    filters.search?.trim() ||
    filters.assigned_to ||
    filters.seller_id ||
    filters.status ||
    filters.priority ||
    filters.probability_min !== undefined ||
    filters.probability_max !== undefined ||
    filters.source ||
    // v1.1.0: tags também devem habilitar as contagens filtradas
    (filters.tag_ids && filters.tag_ids.length > 0) ||
    filters.product_id ||
    filters.category_id ||
    filters.date_from ||
    filters.date_to ||
    filters.created_from ||
    filters.created_to ||
    filters.won_date_from ||
    filters.won_date_to ||
    filters.lost_date_from ||
    filters.lost_date_to ||
    filters.phase_updated_from ||
    filters.phase_updated_to
  );
}

/**
 * Hook para buscar contagens reais (COUNT/SUM) por fase com filtros aplicados
 * Só é habilitado quando há filtros ativos para evitar query desnecessária
 *
 * @param pipelineId - ID do pipeline
 * @param filters - Filtros ativos do Kanban
 */
export function useKanbanColumnCounts(
  pipelineId: string | null,
  filters?: DealFilters
) {
  const { currentTenant } = useAuth();
  const filtersActive = hasActiveFilters(filters);

  const query = useQuery({
    queryKey: ['kanban-column-counts', pipelineId, filters],
    queryFn: async () => {
      logger.debug('Buscando contagens filtradas para pipeline:', pipelineId);

      const { data, error } = await supabase.rpc('get_kanban_column_counts', {
        p_pipeline_id: pipelineId!,
        p_tenant_hint: currentTenant?.id || null,
        p_search: filters?.search || null,
        p_assigned_to: filters?.assigned_to || null,
        p_seller_id: filters?.seller_id || null,
        p_status: filters?.status || null,
        p_priority: filters?.priority || null,
        p_probability_min: filters?.probability_min ?? null,
        p_probability_max: filters?.probability_max ?? null,
        p_source: filters?.source || null,
        p_product_id: filters?.product_id || null,
        p_category_id: filters?.category_id || null,
        p_date_from: filters?.date_from || null,
        p_date_to: filters?.date_to || null,
        // v3.0.0: Novos filtros de data
        p_created_from: filters?.created_from || null,
        p_created_to: filters?.created_to || null,
        p_won_date_from: filters?.won_date_from || null,
        p_won_date_to: filters?.won_date_to || null,
        p_lost_date_from: filters?.lost_date_from || null,
        p_lost_date_to: filters?.lost_date_to || null,
        p_phase_updated_from: filters?.phase_updated_from || null,
        p_phase_updated_to: filters?.phase_updated_to || null,
        // v2.2.0 (2026-07-11): Multi-seleção de origens + motivos de perda
        p_source_ids:
          filters?.source_ids && filters.source_ids.length > 0
            ? filters.source_ids
            : null,
        p_loss_reason_ids:
          filters?.loss_reason_ids && filters.loss_reason_ids.length > 0
            ? filters.loss_reason_ids
            : null,
        // v1.1.0 (2026-08-28): Multi-seleção de tags do negócio
        p_tag_ids:
          filters?.tag_ids && filters.tag_ids.length > 0
            ? filters.tag_ids
            : null,
      } as any);

      if (error) {
        logger.error('Erro ao buscar contagens filtradas:', error);
        throw error;
      }

      // Converte array em Map para acesso rápido por phase_id
      const countsMap = new Map<string, { dealCount: number; totalValue: number }>();
      ((data || []) as ColumnCount[]).forEach((row) => {
        countsMap.set(row.phase_id, {
          dealCount: row.deal_count,
          totalValue: row.total_value,
        });
      });

      logger.debug(`Contagens filtradas: ${countsMap.size} fases com deals`);
      return countsMap;
    },
    // Só executa quando há filtros ativos
    enabled: !!pipelineId && !!currentTenant?.id && filtersActive,
    staleTime: 15 * 1000, // 15 segundos (mais curto que cache de stats)
    gcTime: 2 * 60 * 1000, // 2 minutos
  });

  return {
    /** Map<phaseId, { dealCount, totalValue }> com contagens filtradas */
    countsMap: query.data || new Map<string, { dealCount: number; totalValue: number }>(),
    isLoading: query.isLoading,
    /** Se os filtros estão ativos e os counts devem ser usados */
    filtersActive,
  };
}
